/**
 * GA4 Consent Management
 * Implements Google Consent Mode v2 with localStorage persistence.
 */

import type { ConsentConfig, ConsentStatus } from './types';

const CONSENT_STORAGE_KEY = 'sewar_analytics_consent_v1';

// Default to granted for analytics_storage, denied for ad-related storages (restaurant website privacy best practice)
const DEFAULT_CONSENT: ConsentConfig = {
  analytics_storage: 'granted',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
};

/**
 * Reads user consent preference from localStorage or returns default.
 */
export function getAnalyticsConsent(): ConsentStatus {
  if (typeof window === 'undefined') return 'granted';

  try {
    const saved = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (saved === 'granted' || saved === 'denied') {
      return saved;
    }
  } catch {
    // LocalStorage might be disabled in private/strict modes
  }

  return DEFAULT_CONSENT.analytics_storage;
}

/**
 * Sets user analytics consent preference and updates Google Consent Mode.
 */
export function setAnalyticsConsent(granted: boolean): void {
  const status: ConsentStatus = granted ? 'granted' : 'denied';

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, status);
    } catch {
      // Ignore storage errors
    }

    if (typeof (window as any).gtag === 'function') {
      (window as any).gtag('consent', 'update', {
        analytics_storage: status,
      });
    }
  }
}

/**
 * Initializes default Google Consent Mode state before tag loads.
 */
export function getInitialConsentConfig(): ConsentConfig {
  const status = getAnalyticsConsent();
  return {
    ...DEFAULT_CONSENT,
    analytics_storage: status,
  };
}
